
import { defineComponent, PropType, ref } from "vue";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import { swalNotification } from "@/core/helpers/utils";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { IVatTuTrongKhoResponseBase } from "@/core/interfaces/ApiResponses";

import WordBreakColumn from "@/components/shared/work-break-column/WordBreakColumn.vue";
import useVatTu from "@/core/hooks/use-vat-tu";
import VatTuDetailModal from "@/components/vat-tu/VatTuDetailModal.vue";
import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import IconEye from "@/icons/IconEye.vue";
import IconTrash from "@/icons/IconTrash.vue";

interface IThuHoiRowData {
  id_vat_tu: number;
  tai_sd: number;
  mat: number;
  so_luong_phe_lieu: number;
  quy_doi_kg: number;
  ma_vt_thu_hoi: string;
}

export default defineComponent({
  name: "vat-tu-kho-table",
  emits: [
    "on-delete",
    "refresh",
    "on-update-ghi-chu",
    "on-thu-hoi-data-change",
  ],
  components: { Setting, WordBreakColumn, VatTuDetailModal },

  setup() {
    const { push } = useRouterCustom();
    const { isTextDangerColumn, isValidLabel } = useVatTu();
    const selectedVatTuId = ref<number | undefined>(undefined);

    return {
      push,
      isTextDangerColumn,
      isValidLabel,
      selectedVatTuId,
    };
  },

  props: {
    emptyText: {
      type: String,
      default: "Không có dữ liệu vật tư trong kho",
    },
    isDeleteButtonDisplay: {
      type: Boolean,
      default: true,
    },
    showLyDoColumn: {
      type: Boolean,
      default: false,
    },
    showThuHoiColumns: {
      type: Boolean,
      default: false,
    },
    vatTuItems: {
      type: Array as PropType<Array<IVatTuTrongKhoResponseBase>>,
      default: () => [],
    },
  },

  data() {
    return {
      items: this.vatTuItems as IVatTuTrongKhoResponseBase[],
      thuHoiData: [] as IThuHoiRowData[],
    };
  },

  watch: {
    vatTuItems(value) {
      this.items = value;
      if (this.showThuHoiColumns) {
        this.initThuHoiData();
      }
    },
  },

  mounted() {
    if (this.showThuHoiColumns) {
      this.initThuHoiData();
    }
  },

  methods: {
    formatQuantity,
    formatUnitPrice,
    formatAmount,
    calcLineAmount,

    getSettingActions(row: IVatTuTrongKhoResponseBase): SettingAction[] {
      const actions: SettingAction[] = [
        {
          icon: IconEye,
          label: "Chi tiết v/tư",
          tooltip: "Chi tiết v/tư",
          onClick: () => {
            this.selectedVatTuId = row.id;
          },
        },
      ];

      if (this.isDeleteButtonDisplay) {
        actions.push({
          icon: IconTrash,
          label: "Xoá v/tư khỏi kho",
          tooltip: "Xoá v/tư khỏi kho",
          any: ["data:kho:sua"],
          onClick: () => this.onDeleteVatTuClicked(row),
        });
      }

      return actions;
    },

    initThuHoiData() {
      this.thuHoiData = this.items.map((item) => ({
        id_vat_tu: item.id,
        tai_sd: item.pivot.so_luong,
        mat: 0,
        so_luong_phe_lieu: 0,
        quy_doi_kg: 0,
        ma_vt_thu_hoi: item.vat_tu_thu_hoi?.ma_vat_tu ?? "",
      }));
      this.emitThuHoiData();
    },

    emitThuHoiData() {
      this.$emit("on-thu-hoi-data-change", [...this.thuHoiData]);
    },

    recalcTaiSd(index: number) {
      const data = this.thuHoiData[index];
      if (!data) return;

      const soLuong = this.items[index]?.pivot?.so_luong ?? 0;
      data.tai_sd = soLuong - data.mat - data.so_luong_phe_lieu;
      if (data.tai_sd < 0) data.tai_sd = 0;
    },

    onMatChanged(index: number, value: number | string) {
      const data = this.thuHoiData[index];
      if (!data) return;

      if (typeof value === "number" && value < 0) {
        data.mat = 0;
      }

      const soLuong = this.items[index]?.pivot?.so_luong ?? 0;

      if (data.mat > soLuong) {
        swalNotification("Số lượng mất không thể vượt quá Số lượng", "error");
        data.mat = soLuong;
      }

      if (data.mat + data.so_luong_phe_lieu > soLuong) {
        swalNotification(
          "Tổng Mất + Phế liệu không thể vượt quá Số lượng",
          "error"
        );
        data.mat = soLuong - data.so_luong_phe_lieu;
        if (data.mat < 0) data.mat = 0;
      }

      this.recalcTaiSd(index);
      this.emitThuHoiData();
    },

    onSoLuongPheLieuChanged(index: number, value: number | string) {
      const data = this.thuHoiData[index];
      if (!data) return;

      if (typeof value === "number" && value < 0) {
        data.so_luong_phe_lieu = 0;
      }

      const soLuong = this.items[index]?.pivot?.so_luong ?? 0;

      if (data.mat + data.so_luong_phe_lieu > soLuong) {
        swalNotification(
          "Tổng Mất + Phế liệu không thể vượt quá Số lượng",
          "error"
        );
        data.so_luong_phe_lieu = soLuong - data.mat;
        if (data.so_luong_phe_lieu < 0) data.so_luong_phe_lieu = 0;
      }

      if (data.so_luong_phe_lieu === 0) {
        data.quy_doi_kg = 0;
      }

      this.recalcTaiSd(index);
      this.emitThuHoiData();
    },

    onQuyDoiKgChanged(index: number, value: number | string) {
      const data = this.thuHoiData[index];
      if (!data) return;

      if (typeof value === "number" && value < 0) {
        data.quy_doi_kg = 0;
      }

      if (data.so_luong_phe_lieu > 0 && data.quy_doi_kg === 0) {
        swalNotification(
          "Quy đổi (Kg) phải khác 0 khi Số lượng phế liệu > 0",
          "error"
        );
      }

      this.emitThuHoiData();
    },

    donGiaDisplay(index: number) {
      return formatUnitPrice(this.items[index].pivot.don_gia);
    },

    thanhTienDisplay(index: number) {
      return formatAmount(
        calcLineAmount(
          this.items[index].pivot.don_gia,
          this.items[index].pivot.so_luong
        )
      );
    },

    onDeleteVatTuClicked(vatTu: IVatTuTrongKhoResponseBase) {
      this.$emit("on-delete", {
        vatTu,
      });
    },

    handleGhiChuInput({ id, value }: { id: number; value: string }) {
      this.$emit("on-update-ghi-chu", { id, ghi_chu: value });
    },
  },
});
